import React from "react";
import { XIcon } from "@heroicons/react/outline";
import { FormatMoney } from "../../utils/formatMoney";
import { formatDate } from "../../utils/convertDate";

const ViewTransferModal = ({
  isOpen,
  onClose,
  transfer,
  transferDetails,
  loading,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <XIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Scheduled Transfer Details
              </h3>

              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : (
                <div className="space-y-4">
                  {transfer && (
                    <div className="bg-gray-50 p-4 rounded-md">
                      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Source Account Number
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {transfer.sourceAccountNumber || "N/A"}
                          </dd>
                        </div>

                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Source Account Name
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {transfer.sourceAccountName || "N/A"}
                          </dd>
                        </div>

                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Destination Account Number
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {transfer.destinationAccountNumber || "N/A"}
                          </dd>
                        </div>

                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Destination Account Name
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {transfer.destinationAccountName || "N/A"}
                          </dd>
                        </div>

                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Amount
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {FormatMoney(transfer.amount)}
                          </dd>
                        </div>

                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Transfer Type
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {transfer.transferType || "N/A"}
                          </dd>
                        </div>

                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Frequency
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {transfer.frequency || "N/A"}
                          </dd>
                        </div>

                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Status
                          </dt>
                          <dd className="mt-1">
                            <span
                              className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold leading-5 ${
                                transfer.scheduleStatus === "Active"
                                  ? "bg-green-100 text-green-800"
                                  : transfer.scheduleStatus === "Expired"
                                  ? "bg-orange-100 text-orange-800"
                                  : transfer.scheduleStatus === "Disabled"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {transfer.scheduleStatus || "N/A"}
                            </span>
                          </dd>
                        </div>

                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Start Date
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {formatDate(transfer.startDate)}
                          </dd>
                        </div>

                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            End Date
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {formatDate(transfer.endDate)}
                          </dd>
                        </div>

                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Next Run Date
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {formatDate(transfer.nextDebitDate)}
                          </dd>
                        </div>

                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Narration
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {transfer.narration || "N/A"}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  )}

                  {transferDetails && (
                    <div className="bg-blue-50 p-4 rounded-md">
                      <h4 className="text-md font-medium text-gray-900 mb-2">
                        Additional Details
                      </h4>
                      <dl className="grid grid-cols-1 gap-x-4 gap-y-2">
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Transaction Reference
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {transferDetails.reference || "N/A"}
                          </dd>
                        </div>

                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Created Date
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {transferDetails.createdAt
                              ? formatDate(transferDetails.createdAt)
                              : "N/A"}
                          </dd>
                        </div>

                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Last Modified
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {transferDetails.updatedAt
                              ? formatDate(transferDetails.updatedAt)
                              : "N/A"}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewTransferModal;
